import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { switchMap, map, catchError, tap } from 'rxjs/operators';

import * as ServiceActions from './service.actions';
import { of } from 'rxjs';
import { SiteService } from 'src/app/services/site.service';
import { DeviceService } from 'src/app/services/device.service';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'src/app/services/user.service';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { KeycodeCheck } from 'src/app/model/keycode-check';
import { AppState } from '../app.reducer';
import { Store } from '@ngrx/store';
import * as NotificationsActions from '../notifications/notifications.actions';
import { resolveDeviceModelLabels } from 'src/app/modules/shared/utils/device-model-patches';

@Injectable()
export class ServiceEffects {

    checkKeycode$ = createEffect(() => this.actions$.pipe(
        ofType(ServiceActions.checkKeycode),
        switchMap((action) => this.siteService.checkKeycode(action.term).pipe(
            switchMap((resp) => {
                if (!resp.Success || !resp.Data) {
                    return of(ServiceActions.checkKeycodeFailed({
                        errorMessage: this.translate.instant('SEARCH_FAILED'),
                    }));
                }
                return this.enrichKeycodeCheckWithDeviceModel(resp.Data).pipe(
                    map((checkResult) => ServiceActions.checkKeycodeSuccess({ checkResult })),
                );
            }),
            catchError(error => {
                console.error(error);
                return of(ServiceActions.checkKeycodeFailed({ errorMessage: this.translate.instant('AUTH_ERROR') }));
            })
        ))
    ));

    checkPeople$ = createEffect(() => this.actions$.pipe(
        ofType(ServiceActions.checkPeople),
        switchMap((action) => this.userService.checkPeople(action.email).pipe(
            map((resp) => {
                if (resp.Success) {
                    return ServiceActions.checkPeopleSuccess({ checkResult: resp.Data });
                }
                else {
                    return ServiceActions.checkPeopleFailed({ errorMessage: this.translate.instant('SEARCH_FAILED') });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(ServiceActions.checkPeopleFailed({ errorMessage: this.translate.instant('AUTH_ERROR') }));
            })
        ))
    ));

    searchEmail$ = createEffect(() => this.actions$.pipe(
        ofType(ServiceActions.searchEmail),
        switchMap((action) => this.userService.searchEmail(action.term).pipe(
            map((resp) => {
                if (resp.Success) {
                    return ServiceActions.searchEmailSuccess({ emailOptions: resp.Data });
                }
                else {
                    return ServiceActions.searchEmailFailed({ errorMessage: this.translate.instant('SEARCH_FAILED') });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(ServiceActions.searchEmailFailed({ errorMessage: this.translate.instant('AUTH_ERROR') }));
            })
        ))
    ));


    deleteKeycode$ = createEffect(() => this.actions$.pipe(
        ofType(ServiceActions.deleteKeycode),
        switchMap((action) => this.userService.deleteKeycode(action.keycode)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return ServiceActions.deleteKeycodeSuccess({ message: this.translate.instant('SERVICE_DELETE_KEYCODE_SUCCESS') });
                    } else {
                        return ServiceActions.deleteKeycodeFailed({ errorMessage: this.translate.instant('SERVICE_DELETE_KEYCODE_ERROR') });
                    }
                }),
                catchError(() => of(ServiceActions.deleteKeycodeFailed({ errorMessage: this.translate.instant('SERVICE_DELETE_KEYCODE_ERROR') })))
            )
        )
    ));

    deleteUser$ = createEffect(() => this.actions$.pipe(
        ofType(ServiceActions.deleteUser),
        switchMap((action) => this.userService.deleteUser(action.userId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return ServiceActions.deleteUserSuccess({ message: this.translate.instant('SERVICE_DELETE_USER_SUCCESS') });
                    } else {
                        return ServiceActions.deleteUserFailed({ errorMessage: this.translate.instant('SERVICE_DELETE_USER_ERROR') });
                    }
                }),
                catchError(() => of(ServiceActions.deleteUserFailed({ errorMessage: this.translate.instant('SERVICE_DELETE_USER_ERROR') })))
            )
        )
    ));

    setEnQuickEasy$ = createEffect(() => this.actions$.pipe(
        ofType(ServiceActions.setEnQuickEasy),
        switchMap(({ keycode, enable }) => this.userService.setEnQuickEasy(keycode, enable).pipe(
            map((resp) => {
                if (resp.Success) {
                    const messageKey = enable ? 'SERVICE_ENABLE_QUICK_EASY_SUCCESS' : 'SERVICE_DISABLE_QUICK_EASY_SUCCESS';
                    return ServiceActions.setEnQuickEasySuccess({
                        keycode,
                        enable,
                        message: this.translate.instant(messageKey),
                    });
                }
                return ServiceActions.setEnQuickEasyFailed({ errorMessage: this.translate.instant('SERVICE_QUICK_EASY_ERROR') });
            }),
            catchError(() => of(ServiceActions.setEnQuickEasyFailed({ errorMessage: this.translate.instant('SERVICE_QUICK_EASY_ERROR') }))),
        )),
    ));


    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            ServiceActions.searchEmailFailed,
            ServiceActions.checkKeycodeFailed,
            ServiceActions.checkPeopleFailed,
            ServiceActions.deleteKeycodeFailed,
            ServiceActions.deleteUserFailed,
            ServiceActions.setEnQuickEasyFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("SERVICE", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            ServiceActions.deleteKeycodeSuccess,
            ServiceActions.deleteUserSuccess,
            ServiceActions.setEnQuickEasySuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("SERVICE", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    /**
     * Dopo registry by keycode, carica /device-models/model/{modelId} e applica Patches
     * in base a Registry.BoardType per correggere Model.Name (e Vpn dal modello).
     */
    private enrichKeycodeCheckWithDeviceModel(checkResult: KeycodeCheck) {
        const modelId = checkResult.Registry?.ModelId;
        if (modelId == null) {
            return of(checkResult);
        }
        return this.deviceService.getDeviceModel(modelId).pipe(
            map((modelResp) => {
                if (!modelResp.Success || !modelResp.Data) {
                    return checkResult;
                }
                const labels = resolveDeviceModelLabels(
                    modelResp.Data,
                    checkResult.Registry?.BoardType,
                );
                const name = labels.ProductName?.trim()
                    || labels.Description?.trim()
                    || checkResult.Model?.Name;
                return {
                    ...checkResult,
                    Model: {
                        ...checkResult.Model,
                        Name: name,
                        Vpn: modelResp.Data.Vpn ?? checkResult.Model?.Vpn,
                    },
                };
            }),
            catchError((error) => {
                console.error(error);
                return of(checkResult);
            }),
        );
    }

    constructor(
        private actions$: Actions,
        private translate: TranslateService,
        private siteService: SiteService,
        private deviceService: DeviceService,
        private userService: UserService,
        private store: Store<AppState>,
    ) { }
}
